import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterList } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting } from 'src/hooks';
import { lang } from 'src/utils';

import { useManageComponentTagPage } from './hooks/ManageComponentTagPage.hook';
import { ManageComponentTagGridTable } from './modules/manageComponentTagGridTable/manageComponentTagGridTable';

export const ManageComponentTagPage = () => {
  const navigate = useNavigate();

  const { onChangeParams, numberofResults, activeFilters, totalCount } =
    useManageComponentTagPage();

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );

  // Epoch used ONLY to force-remount FilterList on Reset (so the textbox clears then).
  const [filterListEpoch, setFilterListEpoch] = useState(0);
  const bumpEpoch = () => setFilterListEpoch((e) => e + 1);

  // Keep the toggle state in sync with external changes to activeFilters.
  useEffect(() => {
    setIsFuzzySearch(!('name.list' in activeFilters));
  }, [activeFilters]);

  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting([], [], isFuzzySearch);

  // Toggle search mode WITHOUT clearing the input.
  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      // fuzzy (name.regexp) → exact (name.list)
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      // exact (name.list) → fuzzy (name.regexp)
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
    // Do NOT clear textbox here.
  };

  const shouldDisplayResultInfo = numberofResults != null && totalCount != null;
  const resultCountText = `${lang.Showing} ${numberofResults}/${totalCount}`;

  // Reset clears filters AND the textbox.
  const onReset = () => {
    onChangeParams('name.regexp', []);
    onChangeParams('name.list', []);
    setIsFuzzySearch(true);

    // Clear pending input text and force-remount FilterList to wipe its internal input state.
    onChangeKeyword('');
    bumpEpoch();
  };

  return (
    <Grid>
      <Header
        title="Manage component tags"
        backLink={-1}
        actionBar={
          <ActionBar
            title={lang.manageComponentTag}
            numResults={numberofResults ?? 0}
            onHandleClick={() => navigate(PATHS.COMPONENT_TAGS_ADD)}
          />
        }
        breadcrumbItems={[
          { label: 'Components', link: `${PATHS.COMPONENTS}` },
          { label: 'Tags', link: `${PATHS.COMPONENT_TAGS_MANAGE}` },
        ]}
      />

      <Grid container spacing={4}>
        <Grid item xs={12} md={3} mt={3}>
          <FilterList
            key={`filterlist-${filterListEpoch}`} // Remount only after Reset
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
            readOnly
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          {shouldDisplayResultInfo && (
            <StyledTypography paddingTop={4}>
              {resultCountText}
            </StyledTypography>
          )}
          <ManageComponentTagGridTable />
        </Grid>
      </Grid>
    </Grid>
  );
};
